import { browser } from "$app/environment";

/**
 * Marketing pixel loaders (Meta, TikTok, Reddit) in one consent-aware,
 * idempotent code path.
 *
 * Why this exists: the pixels used to be loaded by an inline <svelte:head>
 * script that only ran at initial document load. A first-time visitor who
 * clicked "Accept all" in the cookie banner got NO pixels until their next
 * hard page load - so their entire first session (the one with ViewContent /
 * AddToCart) was never tagged with _fbp, and Meta Website Custom Audiences
 * never built. Calling loadMarketingPixels() both on mount (returning
 * consented visitors) and reactively when marketingConsentStore flips to
 * true (the accept click itself) closes that gap.
 *
 * Internal (SNRGY team) visitors load NO pixels. The /internal page's switch
 * only ever suppressed PostHog, so a teammate who had accepted cookies kept
 * feeding Meta, TikTok and Reddit - i.e. the data ad spend is optimised
 * against, which is worse than the analytics pollution the switch exists for.
 * The flag is set once from +layout.svelte and checked here rather than at the
 * call sites, so a new caller cannot forget it (which is how the gap happened).
 */

const META_PIXEL_ID = "233108733220807";
// Exported because the consent banner expires the pixel's per-id cookie
// (ttcsid_<id>) on withdrawal; one constant, not a copy.
export const TIKTOK_PIXEL_ID = "CQ5V6RJC77U7COFBPKC0";
const REDDIT_PIXEL_ID = import.meta.env.VITE_REDDIT_PIXEL_ID || "";

function hasMarketingConsent() {
  return browser && localStorage.getItem("marketing") === "true";
}

// Set from +layout.svelte via setInternalVisitor(data.isInternal), which the
// server resolves from the signed glorb_internal cookie - never from anything
// the page itself can decide.
let internalVisitor = false;

/** @param {boolean} isInternal */
export function setInternalVisitor(isInternal) {
  internalVisitor = isInternal === true;
}

/** Marketing consent AND not a teammate. Both must hold. */
function shouldTrack() {
  return hasMarketingConsent() && !internalVisitor;
}

function loadMetaPixel() {
  if (window.fbq) return;
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    "script",
    "https://connect.facebook.net/en_US/fbevents.js",
  );
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

function loadTikTokPixel() {
  if (window.ttq) return;
  !(function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = (w[t] = w[t] || []);
    ttq.methods = [
      "page",
      "track",
      "identify",
      "instances",
      "debug",
      "on",
      "off",
      "once",
      "ready",
      "alias",
      "group",
      "enableCookie",
      "disableCookie",
      "holdConsent",
      "revokeConsent",
      "grantConsent",
    ];
    ttq.setAndDefer = function (t, e) {
      t[e] = function () {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (var i = 0; i < ttq.methods.length; i++)
      ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (t) {
      for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
        ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function (e, n) {
      var r = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = r;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      n = document.createElement("script");
      n.type = "text/javascript";
      n.async = !0;
      n.src = r + "?sdkid=" + e + "&lib=" + t;
      e = document.getElementsByTagName("script")[0];
      e.parentNode.insertBefore(n, e);
    };
    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
  })(window, document, "ttq");
}

function loadRedditPixel() {
  if (!REDDIT_PIXEL_ID || window.rdt) return;
  const rdtScript = document.createElement("script");
  rdtScript.src = "https://www.redditstatic.com/ads/v3/index.js";
  rdtScript.async = true;
  rdtScript.onload = () => {
    window.rdt("init", REDDIT_PIXEL_ID);
    window.rdt("track", "PageVisit");
  };
  document.head.appendChild(rdtScript);
}

/**
 * Load all marketing pixels if the visitor has marketing consent and is not
 * internal. Idempotent - safe to call on mount, on consent accept, and on
 * navigation. Each loader fires its own initial page-view event, so a
 * mid-session accept still tags the page the visitor is currently on.
 */
export function loadMarketingPixels() {
  if (!shouldTrack()) return;
  loadMetaPixel();
  loadTikTokPixel();
  loadRedditPixel();
}

/**
 * Fire a page-view on SPA route changes for whichever pixels are loaded.
 * The pixels only see the initial document load by themselves - without
 * this, client-side navigations (most of the session in a SvelteKit app)
 * produce zero PageViews, which is why landing-page-views sat at ~6% of
 * ad link clicks.
 */
export function trackPixelPageView() {
  if (!shouldTrack()) return;
  if (window.fbq) window.fbq("track", "PageView");
  if (window.ttq?.page) window.ttq.page();
  if (window.rdt) window.rdt("track", "PageVisit");
}
